import { useEffect, useState } from 'react';
import {
  listarApuracoesIrpfm, previewIrpfm, apurarIrpfmReq, simularIrpfm,
  listarRendaParalela, criarRendaParalela, apagarRendaParalela,
} from '../api';
import { PageHeader, Loading, Empty, Alert, KPI, Badge } from '../components/common';
import { fmtMoeda, fmtCnpj, anosOpcoes } from '../lib/format';

export default function IrpfmAnual({ clienteId }) {
  const [ano, setAno] = useState(new Date().getFullYear());
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [modal, setModal] = useState(null);  // 'simulador' | 'apurar' | 'rendaParalela'
  const [detalhe, setDetalhe] = useState(null);

  const carregar = () => {
    if (!clienteId) return;
    setLoading(true);
    listarApuracoesIrpfm(clienteId, { ano })
      .then(r => setLista(r.items || []))
      .catch(err => setMsg({ tipo: 'danger', txt: err.message }))
      .finally(() => setLoading(false));
  };

  useEffect(carregar, [clienteId, ano]);

  const verPreview = async (doc) => {
    try {
      const r = await previewIrpfm(clienteId, doc, ano);
      setDetalhe(r);
    } catch (err) {
      setMsg({ tipo: 'danger', txt: err.message });
    }
  };

  if (!clienteId) return <Empty title="Selecione um cliente" />;

  const totalIrpfm = lista.reduce((s, x) => s + Number(x.irpfm_devido || 0), 0);
  const totalRetido = lista.reduce((s, x) => s + Number(x.total_retido_fonte || 0), 0);
  const totalDif = lista.reduce((s, x) => s + Number(x.saldo_a_pagar || 0), 0);

  return (
    <>
      <PageHeader
        title="IRPFM Anual — Imposto Mínimo PF"
        sub="Camada 2 da Lei 15.270/2025 — alíquota efetiva (renda anual − 600.000) ÷ 60.000, máx 10%"
        actions={
          <>
            <button onClick={() => setModal('simulador')}>🧮 Simulador rápido</button>
            <button onClick={() => setModal('rendaParalela')}>💼 Rendas paralelas</button>
          </>
        }
      />

      {msg && <Alert tipo={msg.tipo}>{msg.txt}</Alert>}

      <div className="kpi-grid">
        <KPI label="IRPFM devido" value={fmtMoeda(totalIrpfm)}
             variant="warning" sub={`exercício ${ano}`} />
        <KPI label="Já retido na fonte" value={fmtMoeda(totalRetido)}
             variant="" sub="crédito Camada 1" />
        <KPI label="Saldo (a pagar/restituir)" value={fmtMoeda(totalDif)}
             variant={totalDif > 0 ? 'danger' : totalDif < 0 ? 'success' : ''}
             sub={totalDif > 0 ? 'sócios devem' : totalDif < 0 ? 'sócios a restituir' : 'quitado'} />
        <KPI label="Piso de isenção" value={fmtMoeda(600000)} sub="até este valor, 0%" />
      </div>

      <div className="toolbar">
        <select value={ano} onChange={e => setAno(Number(e.target.value))}>
          {anosOpcoes(5).map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <span className="muted small">
          Apuração agrega dividendos, JCP e rendas paralelas. Recalcule após cadastrar nova renda.
        </span>
      </div>

      <div className="card">
        {loading ? <Loading /> : lista.length === 0 ? (
          <Empty title={`Sem apurações em ${ano}`}
                 sub="Cadastre um sócio em dividendos ou rode preview a partir dos lançamentos." />
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Sócio (PF)</th>
                <th className="num">Base anual</th>
                <th className="num">Alíquota efetiva</th>
                <th className="num">IRPFM devido</th>
                <th className="num">Retido fonte</th>
                <th className="num">Saldo</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {lista.map(r => (
                <tr key={r.id}>
                  <td>
                    <div>{r.beneficiario_nome}</div>
                    <div className="small muted">{fmtCnpj(r.beneficiario_doc)}</div>
                  </td>
                  <td className="num">{fmtMoeda(r.base_calculo)}</td>
                  <td className="num bold">{Number(r.aliquota_efetiva).toFixed(2)}%</td>
                  <td className="num">{fmtMoeda(r.irpfm_devido)}</td>
                  <td className="num">{fmtMoeda(r.total_retido_fonte)}</td>
                  <td className="num">
                    <span style={{ color: r.saldo_a_pagar > 0 ? 'var(--danger)'
                                       : r.saldo_a_pagar < 0 ? 'var(--success)'
                                       : 'inherit' }}>
                      {fmtMoeda(r.saldo_a_pagar)}
                    </span>
                  </td>
                  <td>
                    <button className="ghost" onClick={() => verPreview(r.beneficiario_doc)}>Detalhe</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {detalhe && <DetalheApuracao apuracao={detalhe} onClose={() => setDetalhe(null)} />}
      {modal === 'simulador' && <SimuladorIrpfm onClose={() => setModal(null)} />}
      {modal === 'rendaParalela' && (
        <RendasParalelas clienteId={clienteId} ano={ano} onClose={() => setModal(null)} />
      )}
    </>
  );
}

function Modal({ title, children, onClose, wide }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200,
    }} onClick={onClose}>
      <div className="card" style={{ width: wide ? 900 : 640, maxHeight: '88vh', overflow: 'auto' }}
           onClick={e => e.stopPropagation()}>
        <div className="flex mb-2">
          <h2 style={{ margin: 0, fontSize: 16 }}>{title}</h2>
          <div className="toolbar__spacer" />
          <button onClick={onClose}>Fechar</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function DetalheApuracao({ apuracao, onClose }) {
  return (
    <Modal title="Apuração detalhada do IRPFM" onClose={onClose} wide>
      <div className="flex" style={{ gap: 24, fontSize: 12 }}>
        <span>Sócio: <b>{fmtCnpj(apuracao.beneficiario_doc)}</b></span>
        <span>Ano: <b>{apuracao.ano}</b></span>
        <span>Faixa: <Badge tipo={
          apuracao.faixa?.codigo === 'isento' ? 'success' :
          apuracao.faixa?.codigo === 'teto_10' ? 'danger' : 'warning'
        }>{apuracao.faixa?.label}</Badge></span>
      </div>

      <table className="data mt-2">
        <thead>
          <tr><th>Componente</th><th className="num">Valor</th></tr>
        </thead>
        <tbody>
          <tr><td>Dividendos</td><td className="num">{fmtMoeda(apuracao.breakdown.dividendos)}</td></tr>
          <tr><td>JCP</td><td className="num">{fmtMoeda(apuracao.breakdown.jcp)}</td></tr>
          <tr><td>Aluguéis</td><td className="num">{fmtMoeda(apuracao.breakdown.alugueis)}</td></tr>
          <tr><td>Ganhos de capital</td><td className="num">{fmtMoeda(apuracao.breakdown.ganhos_capital)}</td></tr>
          <tr><td>Outros isentos</td><td className="num">{fmtMoeda(apuracao.breakdown.outros_isentos)}</td></tr>
          <tr style={{ background: 'var(--surface-2)', fontWeight: 600 }}>
            <td>Base de cálculo (anual)</td>
            <td className="num">{fmtMoeda(apuracao.base_calculo)}</td>
          </tr>
        </tbody>
      </table>

      <div className="card mt-2" style={{ background: 'var(--surface-3)' }}>
        <div className="card__title">Cálculo</div>
        <div style={{ fontFamily: 'monospace', fontSize: 13, lineHeight: 1.8 }}>
          <div>1. Base anual: <b>{fmtMoeda(apuracao.base_calculo)}</b></div>
          <div>2. Fórmula: ({fmtMoeda(apuracao.base_calculo)} − 600.000) ÷ 60.000 = <b>{apuracao.aliquota_efetiva}%</b></div>
          <div>3. IRPFM = {fmtMoeda(apuracao.base_calculo)} × {apuracao.aliquota_efetiva}% = <b style={{ color: 'var(--accent)' }}>{fmtMoeda(apuracao.irpfm_devido)}</b></div>
          <div>4. (−) Retido na fonte: <b>{fmtMoeda(apuracao.total_retido_fonte)}</b></div>
          <div>5. Saldo final: <b style={{
            color: apuracao.saldo_a_pagar > 0 ? 'var(--danger)' :
                   apuracao.saldo_a_pagar < 0 ? 'var(--success)' : 'inherit'
          }}>{fmtMoeda(apuracao.saldo_a_pagar)}</b> ({apuracao.situacao})</div>
        </div>
      </div>
    </Modal>
  );
}

function SimuladorIrpfm({ onClose }) {
  const [campos, setCampos] = useState({
    total_dividendos: 0, total_jcp: 0, total_alugueis: 0,
    total_ganhos_capital: 0, total_outros_isentos: 0, total_retido_fonte: 0,
  });
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState(null);

  const calcular = async () => {
    setErro(null);
    try {
      const r = await simularIrpfm(campos);
      setResultado(r);
    } catch (err) {
      setErro(err.message);
    }
  };

  const set = (k, v) => setCampos(c => ({ ...c, [k]: Number(v) || 0 }));

  return (
    <Modal title="Simulador IRPFM Anual" onClose={onClose} wide>
      <Alert tipo="info">
        Use a fórmula da Lei 15.270/2025: alíquota = (renda anual − 600.000) ÷ 60.000, máx 10%.
        Aplica-se sobre o valor inteiro da base.
      </Alert>

      <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <label>Dividendos anuais
          <input type="number" step="0.01" value={campos.total_dividendos}
                 onChange={e => set('total_dividendos', e.target.value)} />
        </label>
        <label>JCP anual
          <input type="number" step="0.01" value={campos.total_jcp}
                 onChange={e => set('total_jcp', e.target.value)} />
        </label>
        <label>Aluguéis anuais
          <input type="number" step="0.01" value={campos.total_alugueis}
                 onChange={e => set('total_alugueis', e.target.value)} />
        </label>
        <label>Ganhos de capital
          <input type="number" step="0.01" value={campos.total_ganhos_capital}
                 onChange={e => set('total_ganhos_capital', e.target.value)} />
        </label>
        <label>Outros isentos
          <input type="number" step="0.01" value={campos.total_outros_isentos}
                 onChange={e => set('total_outros_isentos', e.target.value)} />
        </label>
        <label>Já retido na fonte
          <input type="number" step="0.01" value={campos.total_retido_fonte}
                 onChange={e => set('total_retido_fonte', e.target.value)} />
        </label>
      </div>

      <div className="flex mt-2">
        <button className="primary" onClick={calcular}>Calcular</button>
      </div>

      {erro && <Alert tipo="danger">{erro}</Alert>}

      {resultado && (
        <div className="card mt-2" style={{ background: 'var(--surface-3)' }}>
          <div className="card__title">Resultado</div>
          <div className="kpi-grid">
            <KPI label="Base anual" value={fmtMoeda(resultado.base_calculo)} />
            <KPI label="Alíquota efetiva" value={`${resultado.aliquota_efetiva}%`} variant="warning" />
            <KPI label="IRPFM devido" value={fmtMoeda(resultado.irpfm_devido)} variant="warning" />
            <KPI label="Saldo final" value={fmtMoeda(resultado.saldo_a_pagar)}
                 variant={resultado.saldo_a_pagar > 0 ? 'danger' :
                          resultado.saldo_a_pagar < 0 ? 'success' : ''} />
          </div>
          <div className="small muted mt-1">
            Faixa: {resultado.faixa?.label} · Situação: {resultado.situacao}
          </div>
        </div>
      )}
    </Modal>
  );
}

function RendasParalelas({ clienteId, ano, onClose }) {
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [novo, setNovo] = useState({
    beneficiario_doc: '', beneficiario_nome: '', ano, mes: '',
    tipo: 'aluguel', valor: '', descricao: '',
  });
  const [erro, setErro] = useState(null);

  const carregar = () => {
    setLoading(true);
    listarRendaParalela(clienteId, { ano })
      .then(r => setLista(r.items || []))
      .catch(err => setErro(err.message))
      .finally(() => setLoading(false));
  };
  useEffect(carregar, []);

  const salvar = async (e) => {
    e.preventDefault();
    setErro(null);
    try {
      await criarRendaParalela(clienteId, {
        ...novo,
        valor: Number(novo.valor),
        ano: Number(novo.ano),
        mes: novo.mes ? Number(novo.mes) : undefined,
      });
      setNovo({ ...novo, valor: '', descricao: '' });
      carregar();
    } catch (err) {
      setErro(err.message);
    }
  };

  const apagar = async (id) => {
    if (!confirm('Excluir esta renda paralela?')) return;
    try {
      await apagarRendaParalela(id);
      carregar();
    } catch (err) {
      setErro(err.message);
    }
  };

  return (
    <Modal title={`Rendas paralelas — ${ano}`} onClose={onClose} wide>
      <Alert tipo="info">
        Camada 3 da Lei 15.270/2025: aluguéis, ganhos de capital e outros rendimentos isentos
        do sócio entram na base anual do IRPFM.
      </Alert>

      <form onSubmit={salvar} className="card mb-2">
        <div className="card__title">Cadastrar nova renda</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
          <input placeholder="CPF do sócio" value={novo.beneficiario_doc}
                 onChange={e => setNovo({...novo, beneficiario_doc: e.target.value})} required />
          <input placeholder="Nome" value={novo.beneficiario_nome}
                 onChange={e => setNovo({...novo, beneficiario_nome: e.target.value})} />
          <select value={novo.tipo} onChange={e => setNovo({...novo, tipo: e.target.value})}>
            <option value="aluguel">Aluguel</option>
            <option value="ganho_capital">Ganho de capital</option>
            <option value="outros_isentos">Outros isentos</option>
          </select>
          <input type="number" placeholder="Ano" value={novo.ano}
                 onChange={e => setNovo({...novo, ano: e.target.value})} />
          <input type="number" placeholder="Mês (opcional)" min="1" max="12"
                 value={novo.mes} onChange={e => setNovo({...novo, mes: e.target.value})} />
          <input type="number" step="0.01" placeholder="Valor" value={novo.valor}
                 onChange={e => setNovo({...novo, valor: e.target.value})} required />
          <input placeholder="Descrição" value={novo.descricao}
                 onChange={e => setNovo({...novo, descricao: e.target.value})}
                 style={{ gridColumn: 'span 2' }} />
          <button className="primary" type="submit">Adicionar</button>
        </div>
      </form>

      {erro && <Alert tipo="danger">{erro}</Alert>}

      {loading ? <Loading /> : lista.length === 0 ? (
        <Empty title="Sem rendas paralelas cadastradas" sub={`Para o ano ${ano}`} />
      ) : (
        <table className="data">
          <thead>
            <tr>
              <th>Sócio</th><th>Tipo</th><th>Mês</th>
              <th className="num">Valor</th><th>Descrição</th><th></th>
            </tr>
          </thead>
          <tbody>
            {lista.map(r => (
              <tr key={r.id}>
                <td>{r.beneficiario_nome || fmtCnpj(r.beneficiario_doc)}</td>
                <td><Badge tipo="neutral">{r.tipo}</Badge></td>
                <td>{r.mes || '—'}</td>
                <td className="num">{fmtMoeda(r.valor)}</td>
                <td className="small">{r.descricao || '—'}</td>
                <td><button className="ghost" onClick={() => apagar(r.id)}>Apagar</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Modal>
  );
}
